import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/motion";

const chapters = [
  ["The boy who noticed", "Alex was a shy child — the kind who noticed the people sitting alone. Music and art became his first languages of strength, and somewhere along the way, a quiet curiosity about people and psychology took root and never left."],
  ["Loss at twenty-two", "At twenty-two, Alex lost his younger brother, who was just nineteen. Some losses don't come with lessons attached — but this one began a long, honest education in grief, presence and what actually matters."],
  ["Across waters, home again", "After studying and working in the United States, Alex returned to Malaysia in 2000. What followed were seasons of leadership and service — visible, demanding, formative."],
  ["The quiet season", "Life moved from a highly visible, volunteer-driven organisation into a much quieter life in rural Pahang. In the quiet, Alex discovered two things at once: real resilience — and how easily a person can lose sight of their own strengths."],
  ["Community, and control", "Alex has experienced both life-giving community and dynamics that quietly became something else. He learned, first-hand, the difference between community and control — and why safe environments are non-negotiable for growth."],
  ["Repair and responsibility", "There have been seasons of significant personal and family disruption. Alex speaks about them the way he coaches through them: with measured honesty, responsibility, and a deep belief in the slow, necessary work of repair."],
  ["Choosing the work", "Out of these chapters, Alex became a coach and trainer — and made a deliberate choice: to help other people navigate their own messy middles, so that none of the pain gets wasted."],
];

const fatherhood = [
  ["Love is unconditional and merciful", "The kind that stays when performance doesn't."],
  ["Leadership is walking the talk", "Believing in and serving the ones entrusted to us — with love, courage and compassion."],
  ["A return on failure is possible", "Failure is inevitable. Growth is the dividend, if we stay honest."],
  ["Repair is crucial", "Not optional. The relationship is worth the hard conversation."],
  ["Truth and mercy, together", "One without the other wounds. Held together, they heal."],
  ["Connection before correction", "People receive truth from hearts they feel connected to."],
  ["Presence, assurance, affirmation", "What people need most is rarely advice first."],
  ["Becoming over arrival", "Our children — and our teams — don't need finished people. They need growing ones."],
];

const beliefs = [
  "People are precious.",
  "People need a safe space to grow in — with others.",
  "People fear the unknown and loss.",
  "People often hide when they feel judged.",
  "People can still flourish and grow in the midst of pain.",
  "People become better where there is hope and an environment that inspires transformation.",
  "People need other people to believe in them when others do not.",
  "Community is crucial for sustainable change.",
  "Love heals and unlocks what is stuck.",
  "Repair work cannot be avoided.",
  "A person's story can become a source of healing and courage for others.",
];

const credentials = [
  "Maxwell Leadership Certified Trainer",
  "HRDF Train-the-Trainer (TTT) Certified",
  "Certified Associate Coach (CAC) by CCA",
  "Masters in Ministry (MBS)",
  "BSc Management & Psychology (KY)",
];

export default function AboutAlex() {
  useEffect(() => {
    document.title = "About Alex — People Growing People";
  }, []);

  return (
    <main data-testid="about-page" className="pt-20">
      {/* HERO */}
      <section className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <MaskedLine delay={0.1}>
              <span className="text-xs uppercase tracking-[0.3em] text-stone">About Alex</span>
            </MaskedLine>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] mt-6">
              <MaskedLine delay={0.25}>I've lived through some</MaskedLine>
              <MaskedLine delay={0.37}>
                messy middles. <span className="italic text-clay">I'm still becoming.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.55}>
              <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/75 max-w-xl">
                Alex is a coach, trainer and facilitator. He doesn't coach from
                arrival — he walks with people from the middle, because the
                middle is where he has done most of his own growing.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.3}>
              <div className="overflow-hidden border border-line">
                <img
                  src="/alex-portrait.jpg"
                  alt="Alex, looking thoughtfully into the distance"
                  className="w-full aspect-[3/4] object-cover object-right"
                  data-testid="about-hero-photo"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STORY CHAPTERS */}
      <section data-testid="about-story" className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32">
              <Reveal>
                <Overline>The Story So Far</Overline>
                <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mt-6">
                  Chapters, <span className="italic text-moss">not highlights.</span>
                </h2>
                <p className="mt-6 text-sm md:text-base text-ink/60 leading-relaxed max-w-xs">
                  Told with measure and dignity — because the difficult chapters
                  deserve honesty, not spectacle.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-8 md:pl-12 md:border-l md:border-line border-t border-line md:border-t-0">
            {chapters.map(([title, copy], i) => (
              <Reveal key={title} delay={Math.min(i * 0.05, 0.25)}>
                <div data-testid={`about-chapter-${i + 1}`} className="py-9 border-b border-line first:pt-0 md:first:pt-0">
                  <div className="flex items-baseline gap-6">
                    <span className="font-serif text-xl text-clay shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{title}</h3>
                      <p className="mt-4 text-sm md:text-base text-ink/70 leading-relaxed max-w-2xl">{copy}</p>
                    </div>
                  </div>
                </div>
                {i === 3 && (
                  <div className="my-10 overflow-hidden border border-line">
                    <img
                      src="/pahang-home.jpg"
                      alt="Alex's quiet rural home in Pahang at dusk, warm light glowing in the windows"
                      loading="lazy"
                      className="w-full aspect-[16/10] object-cover"
                      data-testid="about-pahang-photo"
                    />
                  </div>
                )}
              </Reveal>
            ))}
            <Reveal delay={0.1}>
              <div className="mt-12 overflow-hidden border border-line">
                <img
                  src="/alex-workshop.jpg"
                  alt="Alex facilitating a workshop in a warm, sunlit room"
                  loading="lazy"
                  className="w-full aspect-[16/10] object-cover"
                  data-testid="about-workshop-photo"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE MENTOR — dark */}
      <section data-testid="about-mentor" className="bg-ink text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-44">
          <Reveal>
            <Overline className="text-paper/50">The One Who Believed</Overline>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-12 mt-8">
            <div className="md:col-span-7">
              <Reveal delay={0.1}>
                <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]">
                  He saw an Alex who needed{" "}
                  <span className="italic text-clay">resuscitation.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="mt-10 text-base md:text-lg leading-relaxed text-paper/75 max-w-2xl">
                  Late in his life, Alex's coach and mentor was fighting Stage 4
                  cancer — and still chose to invest his time, talent and
                  treasure into Alex's development. He saw past the depletion to
                  the strengths underneath. He saw a man who could impact more
                  lives through coaching, communication and training.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-paper/75 max-w-2xl">
                  His belief gave Alex courage, and quietly dismantled
                  self-limiting beliefs one conversation at a time. That
                  experience shaped one of the deepest convictions behind People
                  Growing People.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-5 flex items-end">
              <Reveal delay={0.3}>
                <p className="font-serif italic text-2xl md:text-4xl leading-snug text-paper border-l-2 border-clay pl-8">
                  "Sometimes people need someone to believe in them before they
                  can believe in themselves again."
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* FATHERHOOD & LEADERSHIP */}
      <section data-testid="about-fatherhood" className="border-b border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline>Shaped by Fatherhood</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none mt-6 max-w-3xl">
              What being a father taught Alex about{" "}
              <span className="italic text-moss">growing people.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {fatherhood.map(([t, c], i) => (
              <Reveal key={t} delay={i * 0.05} className="bg-paper">
                <div className="p-8 min-h-[190px] flex flex-col">
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight leading-snug">{t}</h3>
                  <p className="text-sm text-ink/65 mt-4 leading-relaxed">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BELIEFS ABOUT PEOPLE */}
      <section data-testid="about-beliefs" className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <Overline>Convictions</Overline>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mt-6">
                What Alex believes about{" "}
                <span className="italic text-clay">people.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 overflow-hidden border border-line max-w-md">
                <img
                  src="/people-crosswalk.webp"
                  alt="People crossing a city street together in golden morning light"
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover"
                  data-testid="about-candid-photo"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-12 md:border-l md:border-line">
            <ul className="border-t border-line">
              {beliefs.map((b, i) => (
                <Reveal key={b} delay={Math.min(i * 0.04, 0.3)}>
                  <li className="py-5 border-b border-line flex gap-5 items-baseline">
                    <span className="w-1.5 h-1.5 bg-clay shrink-0 translate-y-[-2px]" />
                    <p className="font-serif text-lg md:text-2xl tracking-tight text-ink/85 leading-snug">{b}</p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section data-testid="about-credentials" className="border-b border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight max-w-4xl">
              Upskilled to truly grow people.{" "}
              <span className="italic text-moss">Committed to keep growing.</span>
            </h2>
          </Reveal>
          <div className="mt-14 border-t border-line">
            {credentials.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <div className="flex items-baseline gap-6 md:gap-12 py-6 border-b border-line">
                  <span className="font-serif text-lg text-clay shrink-0">0{i + 1}</span>
                  <p className="font-serif text-xl md:text-3xl tracking-tight text-ink/85">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-moss text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[0.98] max-w-4xl">
              The story isn't finished.{" "}
              <span className="italic">Neither is yours.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/work-with-alex"
              data-testid="about-cta-work-with-alex"
              className="group mt-12 inline-flex items-center gap-3 bg-paper text-ink text-xs uppercase tracking-[0.22em] px-10 py-5 hover:bg-clay hover:text-paper transition-colors duration-300"
            >
              Work With Alex
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
